import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { atomFamily, useRecoilState } from 'recoil';
import type { GamepadActionId } from '../generated/graphql';
import { useGamepadBindings } from '../utils/gamepad';
import { useWebHIDBindings } from 'domains/reporter/WebHID/useWebHIDBindings';
import { logger } from 'modules/logger';
import { partialEditor } from '../domains/reporter/RichTextEditor/utils/partialEditor';
import { getSurroundingTextString } from '../domains/reporter/RichTextEditor/utils/getSurroundingTextString';
import { useTypedCharactersActions } from 'hooks/useTypedCharacters';
import { useFocusMode } from './useFocusMode';
import Deferred from 'utils/deferred';
import { INPUT_TYPES } from 'domains/reporter/inputTypes';
import { WithApolloContext } from '../modules/Apollo/types';
import { useSlateStatic } from 'domains/reporter/RichTextEditor/core';

export const mostRecentInputStateFamily = atomFamily<
  {
    isMousePressed: boolean;
    mostRecentInput: string;
  },
  string
>({
  key: 'mousePressedState',
  default: {
    isMousePressed: false,
    mostRecentInput: '',
  },
});

// List of target containers listeners are attached to
export const RICH_TEXT_EDITOR = 'RICH_TEXT_EDITOR';

// NOTE: Hook is somewhat similar to useShouldSkipWebsocketCreation,
// may be possible to refactor and collapse them into a single hook.
export const useMostRecentInput = ({
  id,
  context,
}: { id: string } & WithApolloContext): {
  isMousePressed: boolean;
  mostRecentInput: string;
  setMostRecentInput: (arg1: string) => void;
  setElement: (el: Nullable<HTMLElement>) => void;
  mouseUnpressed: Promise<void>;
} => {
  const [element, setElement] = useState<Nullable<HTMLElement>>(null);
  const [{ isMousePressed, mostRecentInput }, setMostRecentInputAndPressed] = useRecoilState(
    mostRecentInputStateFamily(id)
  );
  const editor = useSlateStatic();

  const isMouseDownGlobal = useRef(false);

  const setMostRecentInput = useCallback(
    (input: string) => {
      setMostRecentInputAndPressed((curr) => {
        if (curr.mostRecentInput === input) {
          return curr;
        }

        return { ...curr, mostRecentInput: input };
      });
    },
    [setMostRecentInputAndPressed]
  );

  const setElementRef = setElement as (el: Nullable<HTMLElement>) => void;

  const { handleTrackingTypedCharacters } = useTypedCharactersActions({ id, context });

  const { isFocusModeSettingEnabled, handleFocusModeMouseClick } = useFocusMode({ context });

  const mouseUnpressedRef = useRef(new Deferred<void>());

  useEffect(() => {
    const handleGlobalMouseDown = () => (isMouseDownGlobal.current = true);
    const handleGlobalMouseUp = () => {
      isMouseDownGlobal.current = false;
      setMostRecentInputAndPressed((curr) => {
        if (!curr.isMousePressed) {
          return curr;
        }

        return { ...curr, isMousePressed: false };
      });
      mouseUnpressedRef.current.resolve();
      mouseUnpressedRef.current = new Deferred<void>();
    };

    document.addEventListener('mousedown', handleGlobalMouseDown);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [setMostRecentInputAndPressed, mouseUnpressedRef]);

  useEffect(() => {
    if (!(element instanceof HTMLElement)) return;
    const currentElement = element;

    const handleMouseDown = (event: MouseEvent) => {
      if (editor != null) {
        const surroundingTextString = getSurroundingTextString(editor);
        logger.info(`[useMostRecentInput] mouseDown at: "${surroundingTextString}." `, {
          editor: editor != null ? partialEditor(editor) : 'null',
        });
      }

      if (isFocusModeSettingEnabled === true) {
        handleFocusModeMouseClick();
      }

      // A press that originates inside the hovering toolbar (e.g. clicking the
      // Bold/Italic buttons) is not a drag-selection inside the editor body.
      // Treating it as one would set isMousePressed=true on mousedown and back
      // to false on mouseup, hiding then re-showing the toolbar — a visible
      // flicker. Record the input as mouse but leave isMousePressed untouched.
      const isToolbarInteraction =
        event.target instanceof Element && event.target.closest('[data-hovering-toolbar]') != null;

      setMostRecentInputAndPressed((curr) => {
        const nextIsMousePressed = isToolbarInteraction ? curr.isMousePressed : true;

        if (
          curr.mostRecentInput === INPUT_TYPES.MOUSE &&
          curr.isMousePressed === nextIsMousePressed
        ) {
          return curr;
        }

        return {
          ...curr,
          mostRecentInput: INPUT_TYPES.MOUSE,
          isMousePressed: nextIsMousePressed,
        };
      });
    };

    const handleMouseUp = () => {
      if (editor != null) {
        const surroundingTextString = getSurroundingTextString(editor);
        logger.info(`[useMostRecentInput] mouseUp at: "${surroundingTextString}." `, {
          editor: editor != null ? partialEditor(editor) : 'null',
        });
      }
      setMostRecentInputAndPressed((curr) => {
        if (curr.mostRecentInput === INPUT_TYPES.MOUSE && !curr.isMousePressed) {
          return curr;
        }

        return {
          ...curr,
          mostRecentInput: INPUT_TYPES.MOUSE,
          isMousePressed: false,
        };
      });
      mouseUnpressedRef.current.resolve();
      mouseUnpressedRef.current = new Deferred<void>();
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (editor != null) {
        const surroundingTextString = getSurroundingTextString(editor);
        logger.info(
          `[useMostRecentInput] keyDown "${event.key} (code ${event.keyCode})" at: "${surroundingTextString}." `,
          {
            editor: editor != null ? partialEditor(editor) : 'null',
          }
        );
      }

      setMostRecentInputAndPressed((curr) => {
        if (curr.mostRecentInput === INPUT_TYPES.KEYBOARD) {
          return curr;
        }

        return { ...curr, mostRecentInput: INPUT_TYPES.KEYBOARD };
      });

      handleTrackingTypedCharacters({ event, editor });
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (editor != null) {
        const surroundingTextString = getSurroundingTextString(editor);
        logger.info(
          `[useMostRecentInput] keyUp "${event.key} (code ${event.keyCode})" at: "${surroundingTextString}." `,
          {
            editor: editor != null ? partialEditor(editor) : 'null',
          }
        );
      }
    };

    currentElement.addEventListener('mousedown', handleMouseDown);
    currentElement.addEventListener('mouseup', handleMouseUp);
    currentElement.addEventListener('keydown', handleKeydown);
    currentElement.addEventListener('keyup', handleKeyUp);
    return () => {
      currentElement.removeEventListener('mousedown', handleMouseDown);
      currentElement.removeEventListener('mouseup', handleMouseUp);
      currentElement.removeEventListener('keydown', handleKeydown);
      currentElement.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    element,
    editor,
    setMostRecentInputAndPressed,
    handleTrackingTypedCharacters,
    handleFocusModeMouseClick,
    isFocusModeSettingEnabled,
    mouseUnpressedRef,
  ]);

  const gamepadCallback = useCallback(
    (params: { action: GamepadActionId; pressed: boolean }) => {
      setMostRecentInputAndPressed((curr) => {
        if (curr.mostRecentInput === INPUT_TYPES.GAMEPAD) {
          return curr;
        }

        return { ...curr, mostRecentInput: INPUT_TYPES.GAMEPAD };
      });
    },
    [setMostRecentInputAndPressed]
  );
  useGamepadBindings({ callback: gamepadCallback, context });
  useWebHIDBindings({ callback: gamepadCallback });

  const memoizedValue = useMemo(() => {
    return {
      isMousePressed,
      mostRecentInput,
      setMostRecentInput,
      setElement: setElementRef,
      mouseUnpressed: mouseUnpressedRef.current.promise,
    };
  }, [isMousePressed, mostRecentInput, setMostRecentInput, setElementRef]);

  return memoizedValue;
};
